import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ShieldCheck, Lock, ArrowRight, CheckCircle2,
  User, Calendar, Hash, Info,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";

// ─────────────────────────────────────────────────────────────────────────────
// PREVIEW: Owner KYC inserted into the Register wizard
// Route: /preview/wallet-kyc  (public, non-destructive mockup)
// ─────────────────────────────────────────────────────────────────────────────
// User instruction: KYC must happen at REGISTRATION — not at wallet activation.
// This preview maps the new step into the existing Register.jsx wizard:
//
//   BUYER  signup:  ① You → ② Business → ③ NEW: Owner KYC → done
//   SELLER signup:  ① You → ② Business → ③ NEW: Owner KYC → ④ Brand → done
//
// Fields captured: legal owner full name · DOB · last 4 of SSN · consent.
// ─────────────────────────────────────────────────────────────────────────────
export default function WalletKycPreview() {
  const loc = useLocation();
  const nav = useNavigate();
  const validTabs = ["buyer", "seller", "form"];
  const initial = validTabs.includes((loc.hash || "").replace("#", ""))
    ? (loc.hash || "").replace("#", "")
    : "buyer";
  const [tab, setTab] = useState(initial);

  // Keep URL hash in sync with tab so each view is shareable.
  useEffect(() => {
    const h = (loc.hash || "").replace("#", "");
    if (validTabs.includes(h) && h !== tab) setTab(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.hash]);

  const switchTab = (k) => {
    setTab(k);
    nav(`/preview/wallet-kyc#${k}`, { replace: false });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-8 py-10">
          <div className="text-[10px] mono uppercase tracking-[0.25em] text-foreground/45">
            PROPOSAL PREVIEW · NOT YET LIVE
          </div>
          <h1 className="display text-4xl sm:text-5xl mt-2 tracking-[-0.02em]">
            Beneficial Owner KYC at sign-up
          </h1>
          <p className="text-foreground/60 mt-3 max-w-2xl text-[15px] leading-relaxed">
            Because TRAQO holds <span className="font-medium text-foreground">real bank routing &amp; account
            numbers</span> on behalf of buyers and sellers, our banking partner requires KYC on the
            <span className="font-medium text-foreground"> legal owner</span> at <span className="font-medium text-foreground">registration</span>,
            before the account is created. This preview shows where the new step slots into the existing
            Register wizard.
          </p>
          <div className="mt-5 flex flex-wrap gap-2 text-[11px] mono uppercase tracking-widest">
            <Pill>Legal owner full name</Pill>
            <Pill>Date of birth</Pill>
            <Pill>Last 4 of SSN</Pill>
            <Pill tone="emerald">Bank-grade encryption</Pill>
            <Pill tone="emerald">No hard credit pull</Pill>
          </div>
        </div>
      </header>

      {/* Persona toggle */}
      <div className="max-w-6xl mx-auto px-8 mt-10">
        <div className="inline-flex gap-1 bg-secondary p-1 rounded-full border border-border">
          {[
            { k: "buyer",  l: "Buyer registration" },
            { k: "seller", l: "Seller registration" },
            { k: "form",   l: "The KYC step UI" },
          ].map(t => (
            <button key={t.k} onClick={() => switchTab(t.k)}
              data-testid={`tab-${t.k}`}
              className={`text-sm rounded-full px-5 py-2 transition-all ${
                tab === t.k
                  ? "bg-foreground text-background"
                  : "text-foreground/65 hover:text-foreground"
              }`}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-8 py-10">
        {tab === "buyer"  && <BuyerSignup  />}
        {tab === "seller" && <SellerSignup />}
        {tab === "form"   && <FormPreview  />}
      </div>

      {/* Footer */}
      <div className="border-t border-border mt-10">
        <div className="max-w-6xl mx-auto px-8 py-8 flex items-center justify-between flex-wrap gap-4">
          <div className="text-[12.5px] text-foreground/60 max-w-xl">
            Approve this proposal and I'll wire it end-to-end: new wizard step in
            <code className="text-foreground"> /register</code>, encrypted backend storage (last-4 only,
            never full SSN), audit log, and admin review tools.
          </div>
          <Link to="/" className="text-sm text-foreground/55 hover:text-foreground self-center">Back to home</Link>
        </div>
      </div>
    </div>
  );
}

// ─── Buyer signup wizard ────────────────────────────────────────────────────
function BuyerSignup() {
  return (
    <section data-testid="buyer-signup" className="space-y-6">
      <FlowHeader
        title="Buyer registration — 3-step wizard with new KYC step"
        subtitle="The new Owner KYC step is inserted between 'Business' and the final submit. The buyer cannot create an account without completing it."
        path="/register?as=buyer  →  ① You  →  ② Business  →  ③ NEW: Owner KYC  →  Account created  →  /buyer/onboarding"
      />

      <Stepper
        steps={[
          { n: "1", label: "You",       sub: "Name & email",        kind: "existing" },
          { n: "2", label: "Business",  sub: "Company, EIN, password", kind: "existing" },
          { n: "3", label: "Owner KYC", sub: "Legal owner ID check", kind: "new" },
        ]}
      />

      <div className="grid lg:grid-cols-3 gap-4">
        <FlowCard step="1" kind="existing" title="Today: Step 1 — You" location="Existing">
          <FormPeek lines={["First name", "Last name", "Work email"]} cta="Continue"/>
        </FlowCard>
        <FlowCard step="2" kind="existing" title="Today: Step 2 — Business" location="Existing">
          <FormPeek lines={["Company name", "EIN (12-3456789)", "Password"]} cta="Get instant credit"/>
        </FlowCard>
        <FlowCard step="3" kind="new" title="NEW: Step 3 — Owner KYC" location="Inserted before submit" highlight>
          <MiniKycCard cta="Verify & create account"/>
        </FlowCard>
      </div>

      <RuleBox>
        <strong>Insertion point:</strong> Today the buyer's submit happens at the end of Step 2. With this
        change, Step 2's "Continue" button advances to a new <strong>Step 3 — Owner KYC</strong>; the actual
        account-creation API call only fires after the owner is verified. Buyer KYB on
        <code> /buyer/onboarding</code> still happens after login as a separate flow.
      </RuleBox>
    </section>
  );
}

// ─── Seller signup wizard ───────────────────────────────────────────────────
function SellerSignup() {
  return (
    <section data-testid="seller-signup" className="space-y-6">
      <FlowHeader
        title="Seller registration — 4-step wizard with new KYC step"
        subtitle="The new Owner KYC step is inserted between 'Business' and the existing 'Brand' step. Seller cannot complete sign-up without it."
        path="/register?as=seller  →  ① You  →  ② Business  →  ③ NEW: Owner KYC  →  ④ Brand  →  Account created  →  /seller"
      />

      <Stepper
        steps={[
          { n: "1", label: "You",       sub: "Name & email",            kind: "existing" },
          { n: "2", label: "Business",  sub: "Company, EIN, password",  kind: "existing" },
          { n: "3", label: "Owner KYC", sub: "Legal owner ID check",    kind: "new" },
          { n: "4", label: "Brand",     sub: "Trade name, GMV, country", kind: "existing" },
        ]}
      />

      <div className="grid lg:grid-cols-4 gap-3">
        <FlowCard step="1" kind="existing" title="Step 1 — You" location="Existing">
          <FormPeek lines={["First name", "Last name", "Work email"]} cta="Continue"/>
        </FlowCard>
        <FlowCard step="2" kind="existing" title="Step 2 — Business" location="Existing">
          <FormPeek lines={["Company", "EIN", "Password"]} cta="Continue"/>
        </FlowCard>
        <FlowCard step="3" kind="new" title="NEW: Step 3 — Owner KYC" location="Inserted" highlight>
          <MiniKycCard cta="Verify & continue"/>
        </FlowCard>
        <FlowCard step="4" kind="existing" title="Step 4 — Brand" location="Existing (was step 3)">
          <FormPeek lines={["Trade name", "Year founded", "Annual GMV", "Brand category"]} cta="Open seller account"/>
        </FlowCard>
      </div>

      <RuleBox>
        <strong>Insertion point:</strong> Same idea — KYC sits right after Business, before Brand. This way
        we verify the legal owner before any commercial data is captured. Today's seller wizard becomes a
        <strong> 4-step</strong> flow.
      </RuleBox>
    </section>
  );
}

// ─── Actual form preview ────────────────────────────────────────────────────
function FormPreview() {
  const [name, setName] = useState("");
  const [dob,  setDob ] = useState("");
  const [ssn,  setSsn ] = useState("");
  const [agree, setAgree] = useState(false);

  return (
    <section data-testid="form-preview" className="grid lg:grid-cols-[1.1fr_1fr] gap-6">
      {/* The step mockup */}
      <div className="rounded-3xl bg-white border border-border shadow-sm p-7" data-testid="kyc-form-card">
        <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 flex items-center gap-2">
          <span>① You</span><span className="w-5 h-px bg-foreground/20"/>
          <span>② Business</span><span className="w-5 h-px bg-foreground/20"/>
          <span className="text-foreground font-semibold">③ Owner KYC</span>
        </div>

        <div className="flex items-center gap-3 mt-4">
          <div className="w-10 h-10 rounded-xl bg-foreground text-background grid place-items-center">
            <ShieldCheck className="w-5 h-5"/>
          </div>
          <div>
            <h2 className="display text-2xl tracking-tight">Verify the legal owner</h2>
            <div className="text-[11px] text-foreground/55">One-time, required by our banking partner.</div>
          </div>
        </div>

        <p className="text-[13px] text-foreground/60 mt-4 leading-relaxed">
          We use this only to confirm identity —
          <strong className="text-foreground"> we never store the full SSN</strong>, only the last 4 digits,
          encrypted at rest.
        </p>

        <div className="mt-6 space-y-4">
          <FormField label="Legal owner full name *" hint="As it appears on government ID" icon={User} testId="kyc-name">
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Jane A. Smith" className="h-11"/>
          </FormField>

          <FormField label="Date of birth *" hint="MM / DD / YYYY · must be 18+" icon={Calendar} testId="kyc-dob">
            <Input type="date" value={dob} onChange={e => setDob(e.target.value)} className="h-11"/>
          </FormField>

          <FormField label="Last 4 digits of SSN *" hint="We never see or store the full number" icon={Hash} testId="kyc-ssn4">
            <Input
              value={ssn}
              onChange={e => setSsn(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="••••"
              inputMode="numeric"
              maxLength={4}
              className="h-11 mono tracking-[0.5em]"
            />
          </FormField>

          <label className="flex items-start gap-3 cursor-pointer pt-2" data-testid="kyc-consent">
            <Checkbox checked={agree} onCheckedChange={v => setAgree(!!v)}/>
            <span className="text-[12.5px] leading-relaxed text-foreground/70">
              I am the beneficial owner (or authorized to act on their behalf) and authorize TRAQO and its
              banking partners to verify my identity using the information above.
            </span>
          </label>
        </div>

        <Button
          disabled={!name || !dob || ssn.length !== 4 || !agree}
          data-testid="kyc-submit"
          className="mt-6 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-40">
          Verify & create account <ArrowRight className="w-4 h-4 ml-1.5"/>
        </Button>
        <div className="mt-2 text-center text-[10px] text-foreground/45 inline-flex items-center justify-center gap-1.5 w-full">
          <Lock className="w-2.5 h-2.5"/>SSL · Bank-grade encryption · last-4 only
        </div>
      </div>

      {/* Why panel */}
      <div className="rounded-3xl bg-foreground text-background p-7 relative overflow-hidden">
        <div className="text-[10px] mono uppercase tracking-[0.25em] text-accent">Why we ask at sign-up</div>
        <h3 className="display text-3xl mt-2 leading-[1.1] tracking-[-0.02em]">
          Required by our bank partner — <span className="text-accent">USA PATRIOT Act §326</span>
        </h3>

        <ul className="mt-7 space-y-4 text-[13.5px]">
          {[
            { t: "Real money, real rails", d: "Once the account exists, TRAQO holds your live routing and account numbers and moves real ACH. Banks require us to verify the beneficial owner before the account is opened." },
            { t: "Soft check only", d: "This is identity verification — not a hard FICO pull. It will not impact anyone's personal credit score." },
            { t: "Last 4 only, never full", d: "We collect just the last 4 of SSN to match against records. The full SSN is never sent to or stored on TRAQO." },
            { t: "Encrypted in transit + at rest", d: "DOB and SSN-last-4 are encrypted with AES-256, only decrypted by our bank partner during verification." },
            { t: "One-time", d: "Submit at sign-up and you're done — no re-verification unless the legal owner of the business changes." },
          ].map(s => (
            <li key={s.t} className="flex gap-3">
              <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5"/>
              <div>
                <div className="font-medium text-base">{s.t}</div>
                <div className="text-[12.5px] text-background/60 mt-0.5 leading-relaxed">{s.d}</div>
              </div>
            </li>
          ))}
        </ul>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-accent/15 blur-3xl"/>
      </div>
    </section>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function Pill({ children, tone = "default" }) {
  const cls = tone === "emerald"
    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : "bg-secondary text-foreground/70 border-border";
  return <span className={`chip border ${cls}`}>{children}</span>;
}

function FlowHeader({ title, subtitle, path }) {
  return (
    <div className="rounded-2xl bg-secondary/60 border border-border p-6">
      <h2 className="display text-2xl tracking-tight">{title}</h2>
      <p className="text-sm text-foreground/60 mt-1.5">{subtitle}</p>
      <div className="mt-3 inline-flex items-center gap-2 text-[11px] mono text-foreground/55 bg-white border border-border rounded-full px-3 py-1.5 max-w-full overflow-x-auto">
        <Info className="w-3 h-3 shrink-0"/>{path}
      </div>
    </div>
  );
}

function Stepper({ steps }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {steps.map((s, i) => {
        const isNew = s.kind === "new";
        return (
          <div key={s.n} className="flex items-center gap-2">
            <div className={`flex items-center gap-2 rounded-full border px-3 py-1.5 ${
              isNew
                ? "border-foreground bg-foreground text-background ring-2 ring-accent/40"
                : "border-border bg-white text-foreground/70"
            }`}>
              <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] mono ${
                isNew ? "bg-accent text-foreground" : "bg-secondary text-foreground/65"
              }`}>{s.n}</span>
              <span className="text-[12.5px] font-medium">{s.label}</span>
              <span className={`text-[10.5px] mono uppercase tracking-wider ${
                isNew ? "text-accent" : "text-foreground/45"
              }`}>{isNew ? "NEW" : s.sub}</span>
            </div>
            {i < steps.length - 1 && <span className="text-foreground/25">→</span>}
          </div>
        );
      })}
    </div>
  );
}

function FlowCard({ step, title, location, children, highlight = false, kind }) {
  return (
    <div className={`rounded-2xl border p-5 ${
      highlight ? "border-foreground bg-white ring-2 ring-accent/40" : "border-border bg-white"
    }`}>
      <div className="flex items-center gap-2">
        <span className={`w-6 h-6 rounded-full grid place-items-center text-[11px] mono ${
          highlight ? "bg-foreground text-background" : "bg-secondary text-foreground/70"
        }`}>{step}</span>
        <div className={`text-[10px] mono uppercase tracking-[0.18em] ${
          kind === "new" ? "text-accent" : "text-foreground/50"
        }`}>
          {kind === "new" ? "New step" : "Existing"}
        </div>
      </div>
      <div className="display text-base mt-2 tracking-tight">{title}</div>
      <div className="mt-4">{children}</div>
      <div className="mt-3 text-[10px] mono text-foreground/45 truncate">{location}</div>
    </div>
  );
}

function FormPeek({ lines, cta }) {
  return (
    <div className="rounded-xl bg-white border border-border p-3 space-y-1.5">
      {lines.map((l, i) => (
        <div key={i} className="h-8 rounded-md bg-secondary border border-border px-2 flex items-center text-[11px] text-foreground/55">
          {l}
        </div>
      ))}
      <Button size="sm" className="w-full rounded-full bg-foreground text-background h-8 text-[11px]">
        {cta}
      </Button>
    </div>
  );
}

function MiniKycCard({ cta }) {
  return (
    <div className="rounded-xl bg-white border-2 border-foreground/20 p-3">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5 text-foreground"/>
        <div className="text-[11px] font-medium">Verify legal owner</div>
      </div>
      <div className="mt-2 space-y-1">
        <div className="h-7 rounded-md bg-secondary border border-border px-2 flex items-center text-[10px] text-foreground/55">Full name</div>
        <div className="grid grid-cols-2 gap-1">
          <div className="h-7 rounded-md bg-secondary border border-border px-2 flex items-center text-[10px] text-foreground/55">DOB</div>
          <div className="h-7 rounded-md bg-secondary border border-border px-2 flex items-center text-[10px] text-foreground/55 mono tracking-[0.3em]">SSN ••••</div>
        </div>
      </div>
      <Button size="sm"
        className="mt-2.5 w-full rounded-full bg-foreground text-background h-7 text-[11px]">
        {cta}
      </Button>
    </div>
  );
}

function FormField({ label, hint, icon: Icon, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-[12.5px] flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-foreground/50"/>}
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-[11px] text-foreground/50 mt-1">{hint}</div>}
    </div>
  );
}

function RuleBox({ children }) {
  return (
    <div className="rounded-2xl bg-blue-50 border border-blue-200 p-4 text-[13px] text-blue-900 leading-relaxed">
      <div className="inline-flex items-center gap-1.5 text-[10px] mono uppercase tracking-[0.18em] text-blue-700">
        <Info className="w-3 h-3"/> Insertion logic
      </div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
